import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AIDENTIFIED_MATCHED_BY,
  AIDENTIFIED_SOURCE_LABELS,
  DATE_TIME_FORMAT,
} from '../../../constants';
import { BasicModal, Button, Loader, ModalHeader } from '../../Shared';

const getFieldValue = (fields, name) =>
  fields.find(field => field.name === name)?.value;

const groupFields = fields =>
  fields.reduce((groups, field) => {
    const group = groups.find(({ name }) => name === field.group);
    if (group) {
      group.fields.push(field);
    } else {
      groups.push({ name: field.group, fields: [field] });
    }
    return groups;
  }, []);

const MetaItem = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 rounded-xl bg-base-700 p-3 min-w-0">
    <span className="text-base-800 text-[13px]/[18px]">{label}</span>
    <span className="text-base-1000 font-medium text-[14px]/[20px] break-words">
      {value}
    </span>
  </div>
);

export const AidentifiedFieldsModal = ({
  loadData,
  contactName,
  contactCompany,
  tenant,
  onClose,
}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      setData(await loadData());
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [loadData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groups = useMemo(() => {
    if (!data) return [];

    const text = search.trim().toLowerCase();
    return groupFields(
      data.fields.filter(
        ({ name, value }) =>
          !text ||
          name.toLowerCase().includes(text) ||
          String(value).toLowerCase().includes(text),
      ),
    );
  }, [data, search]);

  const confidenceRank =
    data && getFieldValue(data.fields, 'Overall Confidence Rank');
  const confidenceScore =
    data && getFieldValue(data.fields, 'Overall Confidence Score');

  return (
    <BasicModal open onClose={onClose}>
      <div className="bg-white w-[860px] max-w-full rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <ModalHeader
          title="Aidentified data"
          subtitle={[contactName, contactCompany, tenant && `Client: ${tenant}`]
            .filter(Boolean)
            .join(' · ')}
          onClose={onClose}
        />
        <div className="flex flex-col gap-4 px-5 pb-5">
          {isLoading && (
            <div className="h-[240px] flex justify-center items-center">
              <Loader />
            </div>
          )}
          {isError && (
            <div className="h-[240px] flex flex-col justify-center items-center gap-3">
              <span className="text-base-800 text-[16px]/[24px]">
                Failed to load the data
              </span>
              <Button
                type="button"
                title="Reload"
                variant="blue"
                onClick={fetchData}
              />
            </div>
          )}
          {!isLoading && !isError && !data && (
            <div className="h-[160px] flex justify-center items-center text-base-800 text-[16px]/[24px]">
              This lead has no Aidentified data.
            </div>
          )}
          {!isLoading && !isError && data && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetaItem
                  label="Export"
                  value={[
                    data.fileName,
                    data.exportDate &&
                      moment(
                        data.exportDate,
                        DATE_TIME_FORMAT.YYYY_MM_DD_DASH,
                      ).format(DATE_TIME_FORMAT.MMM_D_YYYY),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <MetaItem
                  label="Matched by"
                  value={`${
                    AIDENTIFIED_MATCHED_BY.find(
                      ({ value }) => value === data.matchedBy,
                    )?.label ?? data.matchedBy
                  } · ${moment(data.uploadedOn).format(DATE_TIME_FORMAT.MMM_D_YYYY)}`}
                />
                <MetaItem
                  label="Overall confidence"
                  value={
                    confidenceRank
                      ? `${confidenceRank}${confidenceScore ? ` (${confidenceScore})` : ''}`
                      : '-'
                  }
                />
                <MetaItem
                  label="Version"
                  value={`Latest of ${data.versions} ${data.versions === 1 ? 'upload' : 'uploads'}`}
                />
              </div>
              <p className="rounded-xl bg-brand-300/15 p-3 text-base-1000 text-[14px]/[20px]">
                Read-only. Showing the {data.fields.length} of{' '}
                {data.fieldsTotal} fields that have values. Area estimates come
                from IRS Zip+4 statistics for the neighborhood, not the
                person&apos;s own figures.
              </p>
              <input
                type="search"
                value={search}
                placeholder="Filter fields"
                aria-label="Filter fields"
                onChange={e => setSearch(e.target.value)}
                className="w-full max-w-[320px] h-10 rounded-full border border-brand-900 px-4 text-base-1000 text-[14px]/[20px] outline-none"
              />
              <div className="flex flex-col gap-3">
                {groups.map(group => (
                  <div
                    key={group.name}
                    className="rounded-xl border border-base-200"
                  >
                    <div className="flex items-center justify-between gap-3 border-b border-base-200 py-2 px-3">
                      <span className="text-base-1000 font-medium text-[14px]/[20px]">
                        {group.name}
                      </span>
                      <span className="text-base-800 text-[13px]/[18px]">
                        {group.fields.length}{' '}
                        {group.fields.length === 1 ? 'field' : 'fields'} with
                        values
                      </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1 p-3">
                      {group.fields.map(({ id, name, value, source }) => (
                        <div
                          key={id}
                          className="flex justify-between gap-3 text-[14px]/[20px] min-w-0"
                        >
                          <span className="text-base-800 shrink-0 max-w-[50%]">
                            {name}
                          </span>
                          <span className="text-base-1000 text-right break-words min-w-0">
                            {String(value)}
                            {AIDENTIFIED_SOURCE_LABELS[source] && (
                              <span className="ml-2 inline-block rounded bg-rejected-500/20 px-1.5 text-[12px]/[18px]">
                                {AIDENTIFIED_SOURCE_LABELS[source]}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {groups.length === 0 && (
                  <span className="text-base-800 text-[14px]/[20px]">
                    No fields match &quot;{search}&quot;
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </BasicModal>
  );
};
